import { Component, Prop, Watch } from 'vue-property-decorator';
import 'vue-select/dist/vue-select.css';
import Vue from 'vue';
import { DurationOption, ParsedResource } from '@bookings/interfaces/calendar';
import { BookingOptionExportObject, OptionGroupsStructure } from '@core/interfaces/option-group';
import { Discipline } from '@centers/interfaces/discipline';
import { SubscriptionCategory } from '@subscriptions/interfaces/subscription-category';
import { Resource } from '@centers/interfaces/resource';
import { extractOptions } from '@core/helpers/option-groups.helper';
import { Observable } from 'rxjs';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';

@Component({
  name: '',
  components: {},
})
export default class PublicBookingFormCalendarOptions extends Vue {
  @Prop(Array) readonly initialDisciplines: Discipline[];
  @Prop(Array) readonly initialResources: Resource[];

  selectedDate = new Date().toISOString().substr(0, 10);

  disciplines: Discipline[] = [];
  selectedDiscipline: Discipline = null;
  disciplinesCols: number = 3;
  showDisciplinesSelect: boolean = false;

  subscriptionCategories: SubscriptionCategory[] = [];
  selectedSubscriptionCategory: SubscriptionCategory = null;
  subscriptionCategoriesLoaded: boolean = false;

  selectedPaymentMethod: Record<string, string> = null;
  // See also computed propertiy paymentMethods

  rawResources: Resource[] = []; // We store here all resources of a disciplines
  resources: ParsedResource[] = []; // Filtered and parsed resources. They are filtered by the payment method selected and the subscription selected.

  selectedDuration: DurationOption = null;
  // See also computed propertiy durations

  bookingOptions: OptionGroupsStructure = null; // We store here the result of the options requested to the API. Has a fixedOptions and variableOptions
  mergedBookingOptions: BookingOptionExportObject = null; // We store here the result of a merge between the fixedOptions and the variableOptions
  bookingOptionsLoaded: boolean = false;
  bookingOptionsSubscription = null; // Subscription object to control the request for get the booking options
  declare slotsChecked: boolean;
  /* Lifecycle hooks */
  // created () { }
  // updated () { }
  // destroyed () { }
  mounted() {
    this.loadDisciplines();
  }

  /**
   * Functions for fill the selectors
   */
  async loadDisciplines(): Promise<Discipline[]> {
    this.disciplines = [];
    const entityPath = this.$bpcApiRouteHelper.getEntityPath('disciplines');

    if (this.initialDisciplines) {
      this.disciplines = this.initialDisciplines;
    } else {
      const params = {
        columns: ['id', 'name', 'code', 'center_id'],
        status: 'active',
        enable_online_booking: 1,
      };
      const config = { params };
      const res = await this.$bpcApi.get(`${entityPath}/all`, config).toPromise();
      this.disciplines = res.data;
    }
    this.disciplinesCols = 3;
    this.showDisciplinesSelect = false;
    if (this.disciplines.length >= 3 || this.disciplines.length <= 4) {
      this.disciplinesCols = 8;
    } else if (this.disciplines.length >= 5 || this.disciplines.length <= 8) {
      this.disciplinesCols = 12;
    } else if (this.disciplines.length >= 8) {
      this.showDisciplinesSelect = true;
    }
    return this.disciplines;
  }
  async loadResources(): Promise<ParsedResource[]> {
    this.resetResources();
    if (this.initialResources) {
      this.rawResources = this.initialResources;
      this.resources = this.parseResources(this.initialResources);
    } else {
      const params = {
        per_page: 500,
        orderBy: 'ordering',
        orderDirection: 'asc',
        status: 'active',
      };

      if (this.disciplineId) {
        params['disciplinesForOnlineBooking__discipline_id'] = this.disciplineId;
      }

      const config = { params };
      const res = await this.$bpcApi
        .get(this.$bpcApiRouteHelper.getEntityPath('resources'), config)
        .toPromise();
      this.rawResources = res.data;
      this.resources = this.parseResources(res.data);
      return res.data;
    }

    return this.resources;
  }
  get durations(): DurationOption[] {
    if (this.mergedBookingOptions && this.mergedBookingOptions.allowed_durations) {
      const durations = this.mergedBookingOptions.allowed_durations.map(duration => {
        return {
          text: this.$t('global.durations.' + duration + 'min') as string,
          value: duration,
        };
      });
      if (durations.length === 1) {
        this.selectedDuration = durations[0];
      }
      return durations;
    }
    return [];
  }

  get paymentMethods(): Record<string, string>[] {
    const paymentMethods = [];
    if (this.mergedBookingOptions) {
      const withSubscriptionOnlyValue = this.mergedBookingOptions.only_with_subscriptions;
      switch (withSubscriptionOnlyValue) {
        case 'both': {
          paymentMethods.push({
            text: this.$t('global.only_with_subscriptions.with_subscription'),
            value: 'subscription_only',
          });
          paymentMethods.push({
            text: this.$t('global.only_with_subscriptions.with_online_payment'),
            value: 'payment_only',
          });
          break;
        }
        case 'subscription_only': {
          paymentMethods.push({
            text: this.$t('global.only_with_subscriptions.with_subscription'),
            value: 'subscription_only',
          });
          break;
        }
        default: {
          paymentMethods.push({
            text: this.$t('global.only_with_subscriptions.with_online_payment'),
            value: 'payment_only',
          });
          break;
        }
      }
      if (paymentMethods.length === 1) {
        this.selectedPaymentMethod = paymentMethods[0];
      }
    }
    return paymentMethods;
  }

  async loadSubscriptionCategories(): Promise<SubscriptionCategory[]> {
    this.resetSubscriptionCategories();
    const entityPath = this.$bpcApiRouteHelper.getEntityPath('subscription-categories');
    const params = {
      contactId: this.currentContactId,
      disciplineId: this.disciplineId,
      date: this.selectedDate,
    };
    const config = { params };
    const res = await this.$bpcApi.get(`${entityPath}/online-booking/all`, config).toPromise();
    this.subscriptionCategories = res.data;
    this.subscriptionCategoriesLoaded = true;
    return this.subscriptionCategories;
  }

  async loadBookingOptions() {
    return new Promise((resolve, reject) => {
      this.resetBookingOptions();
      const fetchEntityPath = `${this.$bpcApiRouteHelper.getEntityPath(
        'option-groups',
        'raw',
      )}/for-entity`;

      const data = {
        entity_type: 'booking',
        fixed_attributes: [
          {
            entity_type: 'center_settings',
            id: this.getTenantId,
          },
          {
            entity_type: 'disciplines',
            id: this.disciplineId,
          },
        ],
        variable_attributes: {
          resources: this.resources.map(resource => {
            return { entity_type: 'resources', id: resource.id };
          }),
          subscription_categories: this.subscriptionCategories.map(category => {
            return { entity_type: 'subscription_categories', id: category.id };
          }),
        },
      };
      const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.post(
        fetchEntityPath,
        data,
      );

      if (this.bookingOptionsSubscription) {
        this.bookingOptionsSubscription.unsubscribe();
      }

      this.bookingOptionsSubscription = observable.subscribe(
        (res: BPCGenericApiResponse) => {
          if (res.data) {
            this.bookingOptionsLoaded = true;
            this.bookingOptions = res.data;
            this.mergedBookingOptions = res.data.fixed_options;
          }
          resolve(res);
        },
        error => {
          console.log(error);
          reject(error);
        },
      );
    });
  }

  get selectedDurationValue(): number {
    return this.selectedDuration?.value;
  }

  get subscriptionCategoryId(): number {
    return this.selectedSubscriptionCategory?.id
      ? Number(this.selectedSubscriptionCategory.id)
      : null;
  }

  get contactSubscriptionsOfSelectedSubscriptionCategory() {
    return this.selectedSubscriptionCategory?.subscriptions || [];
  }

  get disciplineId(): number {
    return this.selectedDiscipline?.id ? Number(this.selectedDiscipline.id) : null;
  }

  get resourceIds(): number[] {
    return this.resources.map(resource => {
      return Number(resource.id);
    });
  }

  onlyWithSubscriptions(): boolean {
    return (
      this.mergedBookingOptions &&
      (this.mergedBookingOptions.only_with_subscriptions === 'subscription_only' ||
        this.mergedBookingOptions.only_with_subscriptions === 'both') &&
      this.selectedPaymentMethod &&
      this.selectedPaymentMethod['value'] == 'subscription_only'
    );
  }

  /**
   * Watches
   */
  @Watch('disciplineId')
  async onDisciplineIdChange(val) {
    this.resetSelectedDuration();
    this.resetSelectedPaymentMethod();
    this.subscriptionCategoriesLoaded = false;
    if (val) {
      await this.loadResources();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const parallelCalls: Promise<any>[] = [
        this.getAllEvents(),
        this.loadSubscriptionCategories(),
      ];
      await Promise.all(parallelCalls);
      await this.loadBookingOptions();
    } else {
      this.resetSubscriptionCategories();
      this.resetBookingOptions();
      this.resetResources();
    }
  }

  @Watch('selectedDate')
  async onSelectedDateChange(val) {
    if (val && this.disciplineId) {
      this.slotsChecked = false;
      await this.getAllEvents();
      this.checkSlotsAvailability();
    }
  }

  @Watch('selectedDurationValue')
  onChangeSelectedDurationValue(val) {
    if (val) {
      this.checkSlotsAvailability();
    }
  }

  @Watch('selectedPaymentMethod')
  onChangeSelectedPaymentMethod(val) {
    // this.selectedDuration = null;
    if (val && val.value === 'subscription_only') {
      if (this.subscriptionCategories.length === 1) {
        this.selectedSubscriptionCategory = this.subscriptionCategories[0];
      }
    } else if (val && val.value === 'payment_only') {
      this.selectedSubscriptionCategory = null;
    }
    this.filterResourcesByPaymentMethod();
    // this.checkSlotsAvailability();
  }

  @Watch('subscriptionCategoryId')
  async onChangeSubscriptionCategoryId(val) {
    this.slotsChecked = false;
    this.selectedDuration = null;
    this.filterResourcesByPaymentMethod();
    if (this.bookingOptions) {
      this.mergedBookingOptions = extractOptions(this.bookingOptions, {
        subscription_categories: this.subscriptionCategoryId,
      });
    }
    this.checkSlotsAvailability();
  }

  /**
   * Aux computed attributes
   */
  get currentContactId(): number {
    return this.$authService.getContactId();
  }
  get getTenantId(): void {
    return this.$store.getters['tenant/selectedTenantId'];
  }
  get withoutSubscriptions(): boolean {
    return (
      this.selectedPaymentMethod &&
      this.selectedPaymentMethod['value'] == 'subscription_only' &&
      !this.resources.length &&
      !this.selectedSubscriptionCategory &&
      !this.subscriptionCategories.length
    );
  }

  get disablePaymentMethodSelector(): boolean {
    return (
      !this.selectedDiscipline || !this.subscriptionCategoriesLoaded || !this.bookingOptionsLoaded
    );
  }
  get disableDurationSelector(): boolean {
    return (
      !this.selectedDiscipline || !this.subscriptionCategoriesLoaded || !this.bookingOptionsLoaded
    );
  }
  get disableSubscriptionCategorySelector(): boolean {
    return !this.selectedDiscipline || !this.subscriptionCategoriesLoaded;
  }

  /**
   * Aux functions
   */

  parseResources(resources: Resource[]) {
    return resources.map(resource => {
      return {
        id: `${resource.id}`,
        title: resource.code,
        name: resource.name,
        ordering: resource.ordering,
      };
    });
  }

  resetSubscriptionCategories() {
    this.subscriptionCategoriesLoaded = false;
    this.selectedSubscriptionCategory = null;
    this.subscriptionCategories = [];
  }
  resetBookingOptions() {
    this.bookingOptionsLoaded = false;
    this.bookingOptions = null;
    this.mergedBookingOptions = null;
    this.bookingOptionsSubscription = null;
  }
  resetResources() {
    this.resources = [];
  }
  resetSelectedDuration() {
    this.selectedDuration = null;
  }
  resetSelectedPaymentMethod() {
    this.selectedPaymentMethod = null;
  }

  filterResourcesByPaymentMethod() {
    if (this.selectedPaymentMethod && this.selectedPaymentMethod['value'] === 'subscription_only') {
      this.filterResourcesWithOfSelectedSubscriptionCategory();
    } else {
      this.filterResourcesWithOnlinePaymentOnly();
    }
  }

  filterResourcesWithOnlinePaymentOnly() {
    let bookingOptions;
    this.resources = this.parseResources(
      this.rawResources.filter(resource => {
        bookingOptions = extractOptions(this.bookingOptions, {
          resources: resource.id,
        });
        return (
          bookingOptions.only_with_subscriptions === 'payment_only' ||
          bookingOptions.only_with_subscriptions === 'both'
        );
      }),
    );
  }
  filterResourcesWithOfSelectedSubscriptionCategory() {
    this.resetResources();
    if (this.selectedSubscriptionCategory) {
      const ids = this.selectedSubscriptionCategory.resources.map(resource => resource.id);
      this.resources = this.parseResources(
        this.rawResources.filter(resource => ids.includes(resource.id)),
      );
    }
  }

  async getAllEvents(): Promise<void> {
    console.log('getAllEvents');
  }
  checkSlotsAvailability() {
    console.log('checkSlotsAvailability');
  }
}
